import { PeriodType } from '@/lib/api/generated/model';
import type {
  AppliedLimit,
  PeriodBounds,
  SpendTotals,
} from '@/lib/api/generated/model';

import { limitHasOwnPeriod } from './limitWindow';
import { formatMoney, formatTokens } from './money';
import { periodLabel } from './periodCopy';
import { forecastSpend, type SpendForecast } from './spendForecast';

/**
 * One sentence of the meter's footer. The money is kept apart from the words so the
 * component can set it in the foreground weight; tests read the joined text.
 */
export interface FooterPart {
  before: string;
  money?: string;
  after: string;
}

export interface MeterFooterInput {
  spend: SpendTotals;
  limit: AppliedLimit | null;
  /** The period the page is reading, which the limit's own period may not be. */
  period: PeriodBounds;
  now?: Date;
  /**
   * The period's tokens and runs, said last (spec 232 FR-003). They were the summary
   * of a Token use card of their own; on the meter they are one more sentence about
   * the same period, and the split by kind waits behind "Token details".
   */
  tokens?: { total: number; runs: number };
}

export interface PeriodForecastInput {
  spend: SpendTotals;
  limit: AppliedLimit | null;
  period: PeriodBounds;
  now?: Date;
}

export interface PeriodForecast extends SpendForecast {
  /** What was spent so far in the window forecast over. */
  spent: number;
  currency: string;
  /** Forecast as a share of the limit, or null with no limit. */
  ratio: number | null;
  /**
   * "about", or "at least" while any run in the period is unmeasured (spec 253): the
   * forecast is computed from the same incomplete figures, so it cannot be an estimate
   * in both directions.
   */
  hedge: 'about' | 'at least';
}

/**
 * The one forecast rule (spec 253, after spec 099): the average day so far, carried to
 * the period's end, over the limit's own period when it has one (spec 216). The meter,
 * its sentence and the chart card (spec 254) all read this.
 */
export function periodForecast({
  spend,
  limit,
  period,
  now,
}: PeriodForecastInput): PeriodForecast | null {
  if (period.period_type === PeriodType.none) return null;
  const ownPeriod = limit !== null && limitHasOwnPeriod(limit, period);
  const spent = ownPeriod ? limit.consumed_amount : spend.amount;
  const forecast = forecastSpend({
    spendAmount: spent,
    limit,
    period: ownPeriod ? limit.period : period,
    now,
  });
  if (!forecast) return null;
  const cap = limit ? Number(limit.amount) : NaN;
  return {
    ...forecast,
    spent: Number(spent),
    currency: spend.currency ?? limit?.currency ?? 'USD',
    ratio:
      Number.isFinite(cap) && cap > 0
        ? Number(forecast.projectedTotal) / cap
        : null,
    hedge: spend.unmeasured_run_count > 0 ? 'at least' : 'about',
  };
}

/**
 * The forecast as one sentence (spec 253): "On pace for about $53.97 of $60 by 1 Nov,
 * $6.03 to spare." / "At this pace the limit would be reached around 28 Oct." / "The
 * limit is reached." With no limit: "$48.91 so far, about $53.97 by 1 Nov."
 */
export function forecastSentence(
  forecast: PeriodForecast,
  limit: AppliedLimit | null,
): FooterPart {
  const { currency, hedge } = forecast;
  const by = shortDay(forecast.endsAt);
  if (limit === null || forecast.ratio === null) {
    return {
      before: `${formatMoney(forecast.spent.toFixed(2), currency)} so far, ${hedge} `,
      money: formatMoney(forecast.projectedTotal, currency),
      after: ` by ${by}.`,
    };
  }
  if (forecast.limitReached) {
    return { before: 'The limit is reached.', after: '' };
  }
  if (forecast.limitReachedOn) {
    return {
      before: `At this pace the limit would be reached around ${shortDay(forecast.limitReachedOn)}.`,
      after: '',
    };
  }
  const spare = Number(limit.amount) - Number(forecast.projectedTotal);
  return {
    before: `On pace for ${hedge} `,
    money: formatMoney(forecast.projectedTotal, currency),
    after: ` of ${formatMoney(limit.amount, limit.currency ?? currency)} by ${by}, ${formatMoney(spare.toFixed(2), currency)} to spare.`,
  };
}

/**
 * The sentences under the meter, in order: the forecast as one sentence (spec 253),
 * the limit's renewal when no forecast already names the period's end, and the
 * period's tokens. Each is left out when it has nothing to say, and an empty list means
 * no footer at all — the lifetime total has no end to forecast toward and no renewal
 * to name.
 *
 * The renewal is the limit's own window (spec 091), never the calendar period the
 * stepper is browsing.
 */
export function meterFooterParts({
  spend,
  limit,
  period,
  now,
  tokens,
}: MeterFooterInput): FooterPart[] {
  const tokenPart: FooterPart[] =
    tokens && tokens.total > 0
      ? [
          {
            before: `${formatTokens(tokens.total)} billed tokens, with cache, across ${tokens.runs === 1 ? '1 run' : `${String(tokens.runs)} runs`}`,
            after: '',
          },
        ]
      : [];
  // The lifetime total has no end to forecast toward and no renewal to name; its
  // tokens are all there is to add.
  if (period.period_type === PeriodType.none) return tokenPart;

  const forecast = periodForecast({ spend, limit, period, now });
  const parts: FooterPart[] = [];
  if (forecast) parts.push(forecastSentence(forecast, limit));
  if (
    !forecast &&
    limit &&
    limit.period.period_type !== PeriodType.none &&
    limit.period.ends_at !== null
  ) {
    parts.push({
      before: `${periodLabel(limit.period.period_type)} limit renews ${shortDay(new Date(limit.period.ends_at))}`,
      after: '',
    });
  }
  return [...parts, ...tokenPart];
}

/** The footer as one line of text, for an accessible name or a test. */
export function footerText(parts: readonly FooterPart[]): string {
  return parts
    .map((part) => `${part.before}${part.money ?? ''}${part.after}`)
    .join(' · ');
}

/**
 * `1 Nov`, in UTC. `forecastSpend` buckets days against the period's UTC boundaries,
 * so a local-zone format could shift the day east of Greenwich; and one formatter for
 * every part means the line never mixes "Nov 1" with "1 Nov".
 */
export function shortDay(date: Date): string {
  return `${String(date.getUTCDate())} ${MONTHS[date.getUTCMonth()]}`;
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
